import * as THREE from 'three';
import { BENCH, clampToWalkable, SEAT_SPOTS } from '@sea/world';
import type { AvatarInstance, HandTarget } from '../avatar/VrmAvatar';

/** VR 校准项 */
export interface VRCalibration {
  /** 身高缩放微调（自动校准之外的手动系数） */
  scale: number;
  /** 手臂伸展系数 */
  reach: number;
  /** 手腕旋转偏移（度）：绕手臂轴滚，手心朝内/外 */
  wristDeg: number;
  /** 手腕偏转（度）：绕手掌左右轴，手腕上下/侧向掰 */
  tiltDeg: number;
  /** 手腕摆向（度）：绕手心法线，左右摆腕 */
  yawDeg: number;
  /** 模型相对头显的偏移（米） */
  offsetX: number;
  offsetY: number;
  offsetZ: number;
  /** 手指弯曲方向取反 */
  curlFlip: boolean;
  /** 肘部极向量取反 */
  elbowFlip: boolean;
  headFlip: boolean;
}
import { AvatarBody } from '../engine/AvatarBody';
import type { CameraRig } from '../engine/CameraRig';
import type { InputSnapshot } from '../platform/input';

const WALK_SPEED = 1.35; // m/s：小船甲板上的步速，别做冲刺
const ACCEL = 14; // 越大越跟手

const scratch = new THREE.Vector2();
const tmpVec = new THREE.Vector3();
const tmpVec2 = new THREE.Vector3();
const UP = new THREE.Vector3(0, 1, 0);
/**
 * 眼睛相对「头骨原点」的偏移（米）：上 9.5 cm、前 9.5 cm。
 * 头骨原点是颈关节，直接在它那儿放相机会卡在围巾/衣领里面。
 */
const EYE_OFFSET = new THREE.Vector3(0, 0.135, -0.02);
/** 长凳顶面高度（Boat.ts 里长凳盒子中心 0.42 + 半厚 0.04），坐下时髋关节落在这上面 */
const SEAT_TOP_Y = 0.46;

/* ---- VR 姿态自动判定（头显离地高度）---- */
/** 低于这个眼高认为坐着 */
const XR_SEATED_MAX = 1.32;
/** 高于这个眼高认为站着 */
const XR_STAND_MIN = 1.46;
/** 需要持续多久才切换，避免低头/伸懒腰被误判 */
const XR_POSTURE_DEBOUNCE = 0.8;
/** 手动按扳机后，这么久内不被自动判定覆盖 */
const XR_MANUAL_GRACE_MS = 3000;
/** 认为"这是站姿"的最小眼高（用于身高校准） */
const XR_CALIBRATE_MIN = 1.4;
/**
 * 身材补偿系数。
 *
 * 纯按"眼高对齐"会显得矮：这个模型是 Q 版比例（眼睛在身高 84%，真人约 93%），
 * 眼高对上时它的头顶反而低于真人头顶。给 5% 补偿让它看起来接近真实身高。
 */
/**
 * 身材补偿系数。
 *
 * 注意别叠加过头：自动缩放是"眼高对齐"，对 Q 版模型本身已经会放大到 ~1.25，
 * 这里只补一点点（Q 版眼睛占身高比例更小，纯对齐会显得矮一丝）。
 * 之前把它当成"把用户手调的 1.25 烘进默认值"来算（1.31），结果叠加成 1.6 倍。
 */
const XR_STATURE_BIAS = 1.3125;

/**
 * 本地玩家：坐 / 站两种姿态 + 甲板小范围走动（架构 §4.1、§4.3）。
 *
 * 关键不变量：
 *  - 位置永远在**船局部坐标**里，且被 `clampToWalkable` 钳住，不可能掉下船；
 *  - 相机是身体 `headAnchor` 的子节点，头的运动必然带动身体（反之亦然）；
 *  - 走路方向相对**相机朝向**，不是相对世界轴。
 */
export class LocalPlayer {
  readonly root = new THREE.Group();
  readonly body = new AvatarBody();

  private seated = true;
  private seatIndex = 0;
  private walkSpeed = 0;
  private readonly velocity = new THREE.Vector2();
  private avatar: AvatarInstance | null = null;

  /**
   * VR 时相机的挂点。
   *
   * 桌面端相机挂在 `headAnchor`（脖子）上，头身必然同步；但脖子节点每帧都会被写入
   * 地平线补偿和走路 bob —— 这些旋转在 VR 里会直接叠加到头显视角上，是最容易晕的做法。
   * 所以进 XR 时把相机挪到这个只跟着「座位」的稳定节点：不继承补偿、不继承 bob。
   */
  private readonly xrAnchor = new THREE.Group();
  private xrActive = false;

  /** XR 身高校准：采样窗口剩余时间与累计值 */
  private xrCalibrating = 0;
  private xrSampleSum = 0;
  private xrSampleCount = 0;
  /** 校准出的用户真实眼高（相对甲板，米） */
  private xrEyeHeight = 0;
  /** 校准后的模型缩放 */
  private xrScale = 1;
  /** 模型自身（未缩放）的眼高：站 / 坐 */
  private modelEyeStanding = 1.354;
  private modelEyeSeated = 1.065;
  /** 玩家真实眼高：站姿 / 坐姿分别学习（坐着当然比站着低） */
  private userEyeStanding = 0;
  private userEyeSeated = 0;
  /** 身体跟踪偏移（不含校准偏移） */
  private readonly xrTrack = new THREE.Vector3();
  /** 头显世界朝向（每帧复用） */
  private readonly headWorldQ = new THREE.Quaternion();
  /** 脚部 IK 目标（XR 用） */
  private readonly footLeft = new THREE.Vector3();
  private readonly footRight = new THREE.Vector3();
  /** 最近一帧的手柄目标（臂展估计要用） */
  private lastHands: { left: { position: THREE.Vector3 } | null; right: { position: THREE.Vector3 } | null } | null =
    null;
  /** 每侧观察到的最大肩→手柄距离（估计真人臂展） */
  private readonly userArmLen = { left: 0, right: 0 };
  /** 已应用的手臂拉伸比例，避免每帧重设 */
  private readonly appliedArmStretch = { left: 1, right: 1 };

  /** 上一次应用缩放时的眼高（用来"学到就立刻重算"） */
  private lastAppliedEye = 0;

  /* 姿态自动判定 */
  private xrEyeNow = 0;
  private xrEyeSmooth = 0;
  private xrSeatedTimer = 0;
  private xrStandTimer = 0;
  private xrManualUntil = 0;

  /** 手动校准项 */
  private vrCal: VRCalibration = {
    scale: 1.1,
    reach: 1.5,
    // 实测：0° 之后再绕手臂轴向内转 90°，再补 180° 与 30° 偏转才是自然握持
    wristDeg: 20,
    tiltDeg: 60,
    yawDeg: 180,
    offsetX: 0,
    offsetY: 0.2,
    offsetZ: 0,
    curlFlip: true,
    elbowFlip: false,
  headFlip: false,
  };

  constructor(private readonly rig: CameraRig, seatIndex = 0) {
    this.root.add(this.body.root);
    this.xrAnchor.name = 'xrAnchor';
    this.body.headAnchor.name = 'headAnchor';
    this.root.add(this.xrAnchor);
    // 相机挂进脖子：这是「头与身体同步」的唯一可靠做法
    this.body.headAnchor.add(rig.camera);
    this.sit(seatIndex);
  }

  /** 进入 VR：相机从脖子挪到稳定挂点（参考系原点 = 甲板/座位脚下） */
  enterXR(): void {
    if (this.xrActive) return;
    this.xrActive = true;
    this.xrAnchor.position.set(0, 0, 0);
    this.xrAnchor.rotation.set(0, 0, 0);
    this.xrAnchor.add(this.rig.camera);
    // 头显会接管机位，本地变换必须是单位阵
    this.rig.camera.position.set(0, 0, 0);
    this.rig.camera.rotation.set(0, 0, 0);
    // 进 VR 自动量一次身高（站好别动），之后也可以手动重校
    this.calibrateXRHeight(1.5);
  }

  exitXR(): void {
    if (!this.xrActive) return;
    this.xrActive = false;
    this.body.headAnchor.add(this.rig.camera);
    this.rig.camera.position.set(0, 0.02, 0.1);
    this.rig.camera.rotation.set(0, 0, 0);
    // 桌面端用模型原本身材
    this.xrScale = 1;
    this.xrCalibrating = 0;
    this.xrTrack.set(0, 0, 0);
    this.avatar?.root.scale.setScalar(1);
  }

  get isXRActive(): boolean {
    return this.xrActive;
  }

  /**
   * 手柄挂点：把手柄对象（`renderer.xr.getController`）挂进来。
   * 它们的位置是"参考系坐标"，而参考系原点就在这个节点上，所以挂这里才能得到正确的世界坐标。
   */
  get xrAnchorNode(): THREE.Object3D {
    return this.xrAnchor;
  }

  /** 双手目标（世界坐标 + 朝向）。VR 手柄追踪用；null 表示回到程序化姿态 */
  setHandTargets(hands: { left: HandTarget | null; right: HandTarget | null } | null): void {
    this.lastHands = hands
      ? {
          left: hands.left ? { position: hands.left.position.clone() } : null,
          right: hands.right ? { position: hands.right.position.clone() } : null,
        }
      : null;
    this.avatar?.setHandTargets(hands);
  }

  /**
   * VR 校准项（对标 VRChat 的 IK 校准）。
   * 身高缩放是自动的，这里提供的是"对不上时的微调旋钮"。
   */
  get vrCalibration(): VRCalibration {
    return { ...this.vrCal };
  }

  setVRCalibration(partial: Partial<VRCalibration>): void {
    this.vrCal = { ...this.vrCal, ...partial };
    const av = this.avatar;
    if (!av) return;
    av.setReachScale(this.vrCal.reach);
    av.setFingerCurlFlip(this.vrCal.curlFlip);
    av.setElbowFlip(this.vrCal.elbowFlip);
    av.setWristOffset(
      (this.vrCal.wristDeg * Math.PI) / 180,
      (this.vrCal.tiltDeg * Math.PI) / 180,
      (this.vrCal.yawDeg * Math.PI) / 180,
    );
    this.applyXRScale();
  }

  /** 当前模型（调试用） */
  get currentAvatar(): AvatarInstance | null {
    return this.avatar;
  }

  /**
   * 调试：脚部 IK 目标 + 模型量出来的踝高（都换算到「甲板局部」坐标系，y=0 就是甲板面）。
   * 验证"脚是不是真的踩在甲板上"时不用猜。桌面端没有脚部 IK，返回 null。
   */
  debugFeet(): {
    ankleHeight: number;
    scale: number;
    left: [number, number, number] | null;
    right: [number, number, number] | null;
  } | null {
    const av = this.avatar;
    if (!av) return null;
    const round = (v: THREE.Vector3): [number, number, number] => [
      +v.x.toFixed(3),
      +v.y.toFixed(3),
      +v.z.toFixed(3),
    ];
    const inXR = this.xrActive;
    return {
      ankleHeight: av.measure.ankleHeight,
      scale: av.root.scale.y,
      left: inXR ? round(this.root.worldToLocal(this.footLeft.clone())) : null,
      right: inXR ? round(this.root.worldToLocal(this.footRight.clone())) : null,
    };
  }

  /**
   * 身高校准：站好后调用，采样约 1.2 s 的头显高度，换算成身材比例去缩放模型。
   *
   * 为什么要缩放而不是把头显硬对到模型头上：这个模型是 Q 版比例（眼睛在身高的 84%），
   * 真人眼睛约在 93%。两边高度根本对不齐——硬对齐的结果就是"人飘在半空"。
   * 所以：**脚钉在甲板**，再把模型整体缩放到与你的身材一致。
   */
  calibrateXRHeight(duration = 1.2): void {
    this.xrCalibrating = duration;
    this.xrSampleSum = 0;
    this.xrSampleCount = 0;
  }

  private finishXRCalibration(): void {
    if (this.xrSampleCount === 0) return;
    const eye = this.xrSampleSum / this.xrSampleCount;
    if (!Number.isFinite(eye) || eye < 0.4) return;
    this.xrEyeHeight = eye;

    // 按当前高度归类到站姿或坐姿
    if (eye > XR_STAND_MIN) this.userEyeStanding = eye;
    else if (eye < XR_SEATED_MAX) this.userEyeSeated = eye;

    this.applyXRScale();
  }

  /**
   * 身高缩放 = 你的眼高 ÷ 模型的眼高。
   *
   * 早先按"真人眼高 = 身高 × 0.93"反推身材再比，误差全进了比例估算里；
   * 直接对**眼高**最实在：校准后你的视线和模型的头部在同一高度。
   * 站/坐两种姿态各有自己的眼高，必须分开记——坐着用站姿的数会把模型放大一圈。
   */
  private applyXRScale(): void {
    const av = this.avatar;
    if (!av) return;
    const modelEye = this.seated ? this.modelEyeSeated : this.modelEyeStanding;
    const userEye = this.seated ? this.userEyeSeated : this.userEyeStanding;
    const base = userEye > 0 ? Math.max(0.6, Math.min(1.8, (userEye / modelEye) * XR_STATURE_BIAS)) : 1;
    this.xrScale = base;
    av.root.scale.setScalar(base * this.vrCal.scale);
  }

  /** 校准是否有效（坐着校准会被拒绝） */
  get xrCalibrationValid(): boolean {
    return this.xrEyeHeight >= XR_CALIBRATE_MIN;
  }

  /**
   * 用头显离地高度判定坐/站。
   * 带滞回（1.32 / 1.46 两条线）和 0.8 s 去抖，避免低头或伸懒腰就来回切。
   * 手动按过扳机后 3 秒内不自动覆盖。
   */
  private updateXRPosture(dt: number): void {
    const k = 1 - Math.exp(-6 * dt);
    this.xrEyeSmooth += (this.xrEyeNow - this.xrEyeSmooth) * k;

    if (performance.now() < this.xrManualUntil) return;

    if (!this.seated) {
      this.xrSeatedTimer = this.xrEyeSmooth < XR_SEATED_MAX ? this.xrSeatedTimer + dt : 0;
      if (this.xrSeatedTimer > XR_POSTURE_DEBOUNCE) {
        this.xrSeatedTimer = 0;
        // 不瞬移，只换姿势：VR 里被强行挪位置很容易晕
        this.seated = true;
        this.velocity.set(0, 0);
        this.walkSpeed = 0;
        this.body.setSeated(true);
        this.applyXRScale(); // 坐姿眼高不同，缩放要跟着换
      }
    } else {
      this.xrStandTimer = this.xrEyeSmooth > XR_STAND_MIN ? this.xrStandTimer + dt : 0;
      if (this.xrStandTimer > XR_POSTURE_DEBOUNCE) {
        this.xrStandTimer = 0;
        this.seated = false;
        this.body.setSeated(false);
        this.applyXRScale();
      }
    }
  }

  /**
   * 只自动**站起**，不自动坐下。
   *
   * 坐姿下如果真人站着（眼高 1.6+），模型会被头显顶到半空，脚永远够不到甲板——
   * 几何上无解。所以这种情况直接站起；但绝不因为低头/下蹲就自动坐下（那是用户明确不要的）。
   */
  private updateXRAutoStand(dt: number): void {
    const k = 1 - Math.exp(-6 * dt);
    this.xrEyeSmooth += (this.xrEyeNow - this.xrEyeSmooth) * k;
    if (!this.seated || performance.now() < this.xrManualUntil) return;
    if (this.xrEyeSmooth > 1.55) {
      this.xrStandTimer += dt;
      if (this.xrStandTimer > 1.5) {
        this.xrStandTimer = 0;
        this.seated = false;
        this.body.setSeated(false);
        this.applyXRScale();
      }
    } else {
      this.xrStandTimer = 0;
    }
  }

  get xrCalibration(): {
    eyeHeight: number;
    scale: number;
    userEyeStanding: number;
    userEyeSeated: number;
    modelEyeStanding: number;
    modelEyeSeated: number;
  } {
    return {
      eyeHeight: this.xrEyeHeight,
      scale: this.xrScale,
      userEyeStanding: this.userEyeStanding,
      userEyeSeated: this.userEyeSeated,
      modelEyeStanding: this.modelEyeStanding,
      modelEyeSeated: this.modelEyeSeated,
    };
  }

  /**
   * 吸附转身（右摇杆）。VR 下转的是参考系朝向（相机随之转，身体朝向再从头显反推），
   * 桌面端直接改视角 yaw。±30°。
   */
  snapTurn(dir: number): void {
    const delta = (-dir * Math.PI) / 6;
    if (this.xrActive) {
      this.xrAnchor.rotation.y += delta;
      // 立刻重算一次，避免身体朝向要等下一帧
      this.rig.syncFromXR(this.root);
    } else {
      this.rig.setYaw(this.rig.heading + delta);
    }
  }

  get isSeated(): boolean {
    return this.seated;
  }

  get speed(): number {
    return this.walkSpeed;
  }

  get attachedAvatar(): AvatarInstance | null {
    return this.avatar;
  }

  /**
   * 把真实模型接成「我自己」。
   *
   * 保留程序化的 `body` 作为**相机载体**（头部锚点已经和视角绑定好），只是把它隐藏；
   * 真实模型的头每帧被对齐到那只眼睛的位置，所以模型的身体/腿自然跟着相机走，
   * 不会出现「模型站在旁边、相机飘在空中」。
   */
  attachAvatar(av: AvatarInstance): void {
    this.body.setMeshesVisible(false);
    this.root.add(av.root);
    this.avatar = av;

    // 先归位（脚在甲板、面朝前），再从这个模型的**真实比例**量出眼高。
    // 不这么做就得用写死的成年人眼高，Q 版模型会被整体顶起来 —— 两个人的头就不一样高了。
    av.root.position.set(0, 0, 0);
    av.root.rotation.y = 0;
    av.setPose(false);
    av.update(0); // setPose 只记状态，姿势在 update 里落，所以要主动跑一帧再量
    this.root.updateMatrixWorld(true);

    const head = av.headBone;
    if (head) {
      const headLocal = this.root.worldToLocal(head.getWorldPosition(tmpVec)).clone();
      const hipsBone = av.vrm.humanoid?.getNormalizedBoneNode('hips') ?? null;
      const hipsLocalY = hipsBone
        ? this.root.worldToLocal(hipsBone.getWorldPosition(tmpVec2)).y
        : headLocal.y - 0.85;
      const torso = headLocal.y - hipsLocalY;

      const eyeStanding = headLocal.y + EYE_OFFSET.y;
      // 坐着：髋落在长凳顶面，头跟着躯干长度走
      const eyeSeated = SEAT_TOP_Y + torso + EYE_OFFSET.y;
      this.modelEyeStanding = eyeStanding;
      this.modelEyeSeated = eyeSeated;
      this.body.setEyeHeights(eyeStanding, eyeSeated);
    }

    // 第一人称：藏掉自己的头/脸/头发/耳朵（镜子渲染时会临时恢复）
    av.setHeadHidden(true);

    // 把手柄相关的校准项应用到新模型上
    this.setVRCalibration({});
  }

  /**
   * 用观察到的最大「肩→手」距离估计真人臂展，再拉伸手臂骨（FinalIK 的 armLengthMlp）。
   * 这个模型是 Q 版比例，臂展只有身高的 23%（真人约 33%），不拉伸的话
   * 手往外一伸模型就到头了——表现为"手臂伸直后要往回拉一截，模型才弯"。
   */
  private updateArmStretch(hands: { left: { position: THREE.Vector3 } | null; right: { position: THREE.Vector3 } | null } | null): void {
    const av = this.avatar;
    if (!av || !hands) return;
    const humanoid = av.vrm.humanoid;
    if (!humanoid) return;

    for (const side of ['left', 'right'] as const) {
      const target = hands[side];
      const upper = humanoid.getNormalizedBoneNode((side + 'UpperArm') as never) ?? null;
      if (!target || !upper) continue;
      const shoulder = upper.getWorldPosition(new THREE.Vector3());
      const reach = shoulder.distanceTo(target.position);
      // 只把"明显伸展"的样本计入臂展估计，弯着手臂的样本不算
      if (reach > this.userArmLen[side]) this.userArmLen[side] = reach;
      else this.userArmLen[side] = this.userArmLen[side] * 0.999 + 0 * 0.001;

      const modelLen = av.armLengths[side];
      if (modelLen <= 0 || this.userArmLen[side] < 0.2) continue;
      const wanted = Math.max(1, Math.min(1.6, this.userArmLen[side] / modelLen));
      if (Math.abs(wanted - this.appliedArmStretch[side]) > 0.02) {
        this.appliedArmStretch[side] = wanted;
        av.setArmStretch(side, wanted);
      }
    }
  }

  /** 模型姿态 + 头对眼对齐 */
  private updateAvatar(dt: number): void {
    const av = this.avatar;
    if (!av) return;

    av.setPose(this.seated);
    av.setLocomotion(this.walkSpeed);
    this.updateArmStretch(this.lastHands);
    av.setHeadPitch(this.rig.lookPitch);

    const head = av.headBone;
    if (head) {
      this.root.updateMatrixWorld(true);
      head.getWorldPosition(tmpVec);
      this.root.worldToLocal(tmpVec);

      const yaw = this.rig.heading;
      const eye = EYE_OFFSET.clone()
        .applyAxisAngle(UP, yaw)
        .multiplyScalar(av.root.scale.y);

      if (this.xrActive) {
        // VR：让模型的「头骨 + 眼偏移」**精确**落在相机上（三轴都对齐）。
        //
        // 之前只做水平跟随、还带 0.45/0.6 的夹紧，结果是：摇杆转身时相机绕着座位转，
        // 身体追不上（或顶在夹紧值上），头和相机就分家了——于是"向后看被自己的身体挡"，
        // 多转几次身体还会被 IK 拉得变形。
        //
        // 这里不用任何累积量：每帧直接算出「误差」并一次性消掉，
        // 所以旋转多少次都不会漂移。校准偏移直接并进目标点。
        this.rig.camera.getWorldPosition(tmpVec2);
        this.root.worldToLocal(tmpVec2);
        tmpVec2.x += this.vrCal.offsetX;
        tmpVec2.y += this.vrCal.offsetY;
        tmpVec2.z += this.vrCal.offsetZ;

        av.root.position.x += tmpVec2.x - (tmpVec.x + eye.x);
        av.root.position.y += tmpVec2.y - (tmpVec.y + eye.y);
        av.root.position.z += tmpVec2.z - (tmpVec.z + eye.z);

        // 头的**朝向**也要跟着头显：yaw 之外还有 pitch/roll。
        // 之前只对齐位置，头是钉住的——镜子里滚头、低头都没有反馈。
        this.rig.camera.getWorldQuaternion(this.headWorldQ);
        av.setHeadRotation(this.headWorldQ);

        // 脚钉在甲板上（成熟做法：头/手驱动上身，脚做 IK 落地，膝盖吸收升降）。
        // 只让身体跟着头走、不锁脚，低头或下蹲时脚就会插进地板里。
        // 坐姿也要锁脚，否则下蹲时整个人（包括脚）一起沉进甲板。
        {
          const scale = av.root.scale.y;
          const yaw = this.rig.heading;
          const rightX = Math.cos(yaw);
          const rightZ = -Math.sin(yaw);
          const fwdX = -Math.sin(yaw);
          const fwdZ = -Math.cos(yaw);
          const halfW = 0.09 * scale;
          // 站着时脚尖略向前；坐着时脚在长凳**前方**（身体朝 −Z，所以偏移要取正）
          const fwdOff = (this.seated ? 0.3 : 0.02) * scale;
          // 踝关节高度用模型自己量出来的值：写死 0.08*缩放的话，
          // Q 版模型 + 放大会让鞋底要么陷进甲板、要么悬空
          const ankleY = (av.measure.ankleHeight || 0.08) * scale;
          this.footLeft.set(
            av.root.position.x - rightX * halfW + fwdX * fwdOff,
            ankleY,
            av.root.position.z - rightZ * halfW + fwdZ * fwdOff,
          );
          this.footRight.set(
            av.root.position.x + rightX * halfW + fwdX * fwdOff,
            ankleY,
            av.root.position.z + rightZ * halfW + fwdZ * fwdOff,
          );
          this.root.localToWorld(this.footLeft);
          this.root.localToWorld(this.footRight);
          av.setFootTargets({ left: this.footLeft, right: this.footRight });
        }
      } else {
        av.setFootTargets(null);
        // 目标：让「头骨原点 + 眼睛偏移」落在相机位置上，而不是让头骨原点落在相机上
        av.root.position.x -= tmpVec.x + eye.x;
        av.root.position.y += this.body.eyeHeight - tmpVec.y - eye.y;
        av.root.position.z -= tmpVec.z + eye.z;
      }
    }

    av.update(dt);
  }

  sit(index = this.seatIndex): void {
    const i = Math.max(0, Math.min(SEAT_SPOTS.length - 1, index));
    const spot = SEAT_SPOTS[i]!;
    this.seatIndex = i;
    this.seated = true;
    this.velocity.set(0, 0);
    this.walkSpeed = 0;
    this.root.position.set(spot.x, 0, spot.z);
    this.body.setSeated(true);
  }

  stand(): void {
    const spot = SEAT_SPOTS[this.seatIndex]!;
    // 起身时向前挪一点，避免和长凳重叠
    const p = clampToWalkable(spot.x, spot.z - 0.5);
    this.seated = false;
    this.root.position.set(p.x, 0, p.z);
    this.body.setSeated(false);
  }

  update(dt: number, input: InputSnapshot): void {
    if (input.sit) {
      // 手动切换后 3 秒内不让自动判定覆盖
      if (this.xrActive) this.xrManualUntil = performance.now() + XR_MANUAL_GRACE_MS;
      if (this.seated) this.stand();
      else this.sit();
    }

    // VR：先把头显姿态换算成视角，下游（身体朝向/姿态上行/眼睛偏移）全部照旧走
    if (this.xrActive) {
      this.rig.syncFromXR(this.root);

      // 头显离地高度：既用于身高校准采样，也用于自动判定坐/站
      this.rig.camera.getWorldPosition(tmpVec2);
      this.root.worldToLocal(tmpVec2);
      this.xrEyeNow = tmpVec2.y;

      // 持续学习两种姿态下的真实眼高（慢速 EMA，抗抖动）
      if (this.xrEyeNow > XR_STAND_MIN) {
        this.userEyeStanding =
          this.userEyeStanding > 0 ? this.userEyeStanding * 0.98 + this.xrEyeNow * 0.02 : this.xrEyeNow;
      } else if (this.xrEyeNow < XR_SEATED_MAX) {
        this.userEyeSeated =
          this.userEyeSeated > 0 ? this.userEyeSeated * 0.98 + this.xrEyeNow * 0.02 : this.xrEyeNow;
      }

      // 学到新高度就立刻重算缩放。
      // 不这么做的话：进 VR 时自动判定先切到"站"，而站姿眼高还没攒够，
      // 缩放会停在 1（模型比人矮一截），之后再也不更新。
      this.updateXRAutoStand(dt);

      const learnedEye = this.seated ? this.userEyeSeated : this.userEyeStanding;
      if (learnedEye > 0 && Math.abs(learnedEye - this.lastAppliedEye) > 0.008) {
        this.lastAppliedEye = learnedEye;
        this.applyXRScale();
      }

      if (this.xrCalibrating > 0) {
        this.xrSampleSum += this.xrEyeNow;
        this.xrSampleCount++;
        this.xrCalibrating -= dt;
        if (this.xrCalibrating <= 0) this.finishXRCalibration();
      }

    }

    const yaw = this.rig.heading;

    // 姿态每帧都从 rig 推导：身体朝向、脖子俯仰、以及地平线锁的补偿
    this.body.setYaw(yaw);
    // VR 里脖子不能再叠加俯仰或补偿，否则和头显姿态打架
    this.body.setLook(this.xrActive ? 0 : this.rig.lookPitch);
    this.body.setCounterTilt(
      this.xrActive ? 0 : this.rig.comfortPitch,
      this.xrActive ? 0 : this.rig.comfortRoll,
    );

    if (this.avatar) {
      this.avatar.root.rotation.y = yaw; // 模型跟着视角转，朝向由自己承担
      this.updateAvatar(dt);
    }

    if (this.seated) {
      this.walkSpeed = 0;
      this.velocity.set(0, 0);
      this.body.update(dt, { speed: 0, seated: true });
      return;
    }

    // 相机朝向 → 船局部方向：forward = (-sin, -cos)，right = (cos, -sin)
    const fx = -Math.sin(yaw);
    const fz = -Math.cos(yaw);
    const rx = Math.cos(yaw);
    const rz = -Math.sin(yaw);

    let dx = fx * input.forward + rx * input.strafe;
    let dz = fz * input.forward + rz * input.strafe;
    const len = Math.hypot(dx, dz);
    if (len > 1) {
      dx /= len;
      dz /= len;
    }

    scratch.set(dx * WALK_SPEED, dz * WALK_SPEED);
    const k = 1 - Math.exp(-ACCEL * dt);
    this.velocity.lerp(scratch, k);

    const nx = this.root.position.x + this.velocity.x * dt;
    const nz = this.root.position.z + this.velocity.y * dt;
    const clamped = clampToWalkable(nx, nz);
    this.root.position.set(clamped.x, 0, clamped.z);
    // 撞到边界就把速度吃掉，避免贴着边一直「推墙」
    if (clamped.clamped) this.velocity.multiplyScalar(0.2);

    this.walkSpeed = Math.hypot(this.velocity.x, this.velocity.y);
    this.body.update(dt, { speed: this.walkSpeed, seated: false });
  }
}
